import { useAtom, useAtomValue } from "jotai";
import { Navbar, Nav } from "react-bootstrap";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { BootstrapBreakpointDebugger } from "./Controls/BootstrapBreakpointDebugger";
import { APIProvider } from "@vis.gl/react-google-maps";
import { authAtom, authTokenAtom, balancesAtom } from "./Data/Atoms";
import { Api } from "./Data/Api";
import { apiServerName } from "./Data/ApiUrl";
import { useMemo, useState } from "react";
import { PageMask } from "./PageMask";
import { ToastContainer } from "react-toastify";
import {
  House,
  QrCodeScan,
  Calendar2Check,
  CarFront,
  ArrowRepeat,
  InboxFill,
  CurrencyDollar,
  CarFrontFill,
  CreditCard,
  X,
  PersonCircle,
  Person,
  PencilSquare,
  InfoCircle,
} from "react-bootstrap-icons";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

import { OrdersMenuBadge } from "./AppUser";
import { cents, toCents } from "./Data/Money";
import { add } from "dinero.js";
import { PendingChangesProvider } from "./PendingChangesContext";
import { SaveChangesBar } from "./SaveChangesBar";

function UserMenu() {
  const nav = useNavigate();
  const [auth, setAuth] = useAtom(authAtom);
  const [authToken, setAuthToken] = useAtom(authTokenAtom);

  function logOut(): void {
    Api.logOut().then((res) => {
      if (res.isOk || (!res.isOk && res.error === "You are not logged in")) {
        setAuth({ isLoggedIn: false });
        setAuthToken({ accessToken: undefined });
        return;
      }
      console.log(`Unexpected error when logging out: ${res.error}`);
    });
  }

  if (auth.isLoggedIn === true && auth.account !== undefined) {
    return (
      <>
        <Nav.Link
          eventKey={1}
          onClick={() => nav("/user/profile")}
          className="ms-5 text-white fs-6"
          style={{ opacity: 1.0 }}
        >
          Profile
        </Nav.Link>
        <Nav.Link
          eventKey={2}
          onClick={() => logOut()}
          className="ms-5 text-white fs-6"
          style={{ opacity: 1.0 }}
        >
          Log Out
        </Nav.Link>
      </>
    );
  }
  return (
    <>
      <Nav.Link
        eventKey={3}
        as={Link}
        to="/login"
        className="ms-5 text-white fs-6"
      >
        Log <span style={{ letterSpacing: "0.075em" }}>I</span>n
      </Nav.Link>
      <Nav.Link
        eventKey={4}
        as={Link}
        to="/signup"
        className="ms-5 text-white fs-6"
      >
        Sign Up
      </Nav.Link>
    </>
  );
}

function TopMenu() {
  const [showMenu, setShowMenu] = useState(false);
  const [auth, setAuth] = useAtom(authAtom);
  const [authToken, setAuthToken] = useAtom(authTokenAtom);
  const nav = useNavigate();

  // Use the balances from the atom instead of fetching
  const balances = useAtomValue(balancesAtom);

  const totalDebt = useMemo(
    () => balances.map((b) => b.balance).reduce((a, b) => add(a, b), cents(0)),
    [balances]
  );

  function logOut(): void {
    Api.logOut().then((res) => {
      if (res.isOk || (!res.isOk && res.error === "You are not logged in")) {
        setAuth({ isLoggedIn: false });
        setAuthToken({ accessToken: undefined });
        setShowMenu(false);
        nav("/login");
        return;
      }
      console.log(`Unexpected error when logging out: ${res.error}`);
    });
  }

  return (
    <>
      <Navbar
        collapseOnSelect
        className="px-5"
        expand="md"
        bg="black"
        variant="dark"
      >
        <Navbar.Brand href="/" className="fs-5 d-flex align-items-center gap-2">
          <img src="/img/logo-spotsync.svg" style={{ width: "1.5em" }} />
          <span>
            <span className="fw-bold">Spot</span>Sync
          </span>
        </Navbar.Brand>
        {apiServerName === "local" && <BootstrapBreakpointDebugger />}

        <Navbar.Toggle
          aria-controls="responsive-navbar-nav"
          className="d-md-none"
          onClick={() => setShowMenu(true)}
        />

        <Navbar.Collapse
          id="responsive-navbar-nav"
          className="justify-content-end d-none d-md-flex"
        >
          <Nav>
            <Nav.Link
              eventKey={5}
              as={Link}
              to="/about"
              className="ms-5 text-white fs-6"
            >
              About
            </Nav.Link>
            <UserMenu />
          </Nav>
        </Navbar.Collapse>
      </Navbar>

      <div className={`slide-over ${showMenu ? "open" : ""}`}>
        <div className="slide-over-header">
          <span className="menu-title">Menu</span>
          <button
            className="btn btn-link text-dark p-0 ms-auto"
            onClick={() => setShowMenu(false)}
          >
            <X size={24} />
          </button>
        </div>

        {auth.isLoggedIn && auth.account && (
          <div className="slide-over-body">
            {[
              {
                label: "Access Passes",
                icon: <QrCodeScan />,
                url: "/user/access-codes",
              },
              {
                label: "Reservations",
                icon: <Calendar2Check />,
                url: "/user/reservation/upcoming",
              },
              { label: "Parkers", icon: <CarFront />, url: "/user/parkers" },
              { label: "Plans", icon: <ArrowRepeat />, url: "/user/plan" },
              {
                label: "Orders",
                icon: <InboxFill />,
                url: "/user/order",
                extra:
                  toCents(totalDebt) > 0 ? (
                    <OrdersMenuBadge balance={totalDebt} />
                  ) : null,
              },
              {
                label: "Payments",
                icon: <CurrencyDollar />,
                url: "/user/payment",
              },
              {
                label: "Vehicles",
                icon: <CarFrontFill />,
                url: "/user/vehicle",
              },
              { label: "Billing", icon: <CreditCard />, url: "/user/billing" },
            ].map((item) => (
              <button
                key={item.label}
                className="slide-over-item"
                onClick={() => {
                  nav(item.url);
                  setShowMenu(false);
                }}
              >
                {item.icon}
                <span className="flex-grow-1">{item.label}</span>
                {item.extra}
              </button>
            ))}
          </div>
        )}

        {auth.isLoggedIn && auth.account ? (
          <div className="slide-over-footer">
            <div
              className="slide-over-item"
              role="button"
              onClick={() => {
                setShowMenu(false);
                nav("/user/profile");
              }}
            >
              <PersonCircle size={18} />
              <span className="flex-grow-1">Profile</span>
            </div>

            <button
              className="logout-btn"
              onClick={() => {
                logOut();
                setShowMenu(false);
              }}
            >
              Log Out
            </button>
          </div>
        ) : (
          <>
            <div>
              <button
                className="slide-over-item"
                onClick={() => {
                  setShowMenu(false);
                  nav("/login");
                }}
              >
                <Person />
                <span className="flex-grow-1 ms-2">Log In</span>
              </button>

              <button
                className="slide-over-item"
                onClick={() => {
                  setShowMenu(false);
                  nav("/signup");
                }}
              >
                <PencilSquare />
                <span className="flex-grow-1 ms-2">Sign Up</span>
              </button>
            </div>

            <div className="about-wrapper mt-auto">
              <button
                className="slide-over-item"
                onClick={() => {
                  setShowMenu(false);
                  nav("/about");
                }}
              >
                <InfoCircle />
                <span className="flex-grow-1 ms-2">About</span>
              </button>
            </div>
          </>
        )}
      </div>

      {showMenu && (
        <div
          className="slide-over-overlay"
          onClick={() => setShowMenu(false)}
        />
      )}
    </>
  );
}

export function App() {
  return (
    <APIProvider
      apiKey="AIzaSyDfdr9BkDEl6lj-Sxoka0CgmAA9R76gH2k"
      libraries={["places"]}
    >
    <PendingChangesProvider>
      <div id="main">
        <TopMenu />
        <div id="content">
          <ToastContainer />
          <Outlet />
        </div>
        <SaveChangesBar />
        <PageMask />
      </div>
    </PendingChangesProvider>
    </APIProvider>
  );
}
